import { z, ZodError } from 'zod';
import { baseMessageSchema, createMessageId } from './_base';

export enum MetricMessageType {
  METRICS_WEBRTC_QUALITY = 'METRICS_WEBRTC_QUALITY',
  METRICS_WEBRTC_QUALITY_CHANGE = 'METRICS_WEBRTC_QUALITY_CHANGE',
  METRICS_ERROR = 'METRICS_ERROR',
}

export enum MetricSourceType {
  PLAYER = 'PLAYER',
  HOST = 'HOST',
}

// Payload schemas
const rawStatsSchema = z.object({
  hasActiveVideoTrack: z.boolean(),
  currentRoundTripTime: z.number(),
  averageDecodeTimePerFrame: z.number(), // Legacy field, kept for backwards compatibility
  jitter: z.number(),
  packetLossPercentage: z.number(),
  bitsPerSecond: z.number(),
  videoKbps: z.number(),
  avgFrameRate: z.number(),
  maxRecentFrameRate: z.number().nullable(),
  minRecentFrameRate: z.number().nullable(),
  // New timing metrics in milliseconds
  averageEncodeTimePerFrameMs: z.number().optional(), // Host/outbound streams only
  averageDecodeTimePerFrameMs: z.number().optional(), // Player/inbound streams only
  averageProcessingDelayPerFrameMs: z.number().optional(), // Player/inbound streams only
  totalEncodeTime: z.number().optional(), // Raw value in seconds
  totalDecodeTime: z.number().optional(), // Raw value in seconds
  totalProcessingDelay: z.number().optional(), // Raw value in seconds
  framesEncoded: z.number().optional(), // Host/outbound streams only
  framesDecoded: z.number().optional(), // Player/inbound streams only
  jitterBufferEmittedCount: z.number().optional(), // Player/inbound streams only
});

const qualityStatSchema = z.object({
  recommendation: z.string(),
  reason: z.string(),
  rawStats: rawStatsSchema,
});

const qualityMetricsPayloadSchema = z.object({
  timestamp: z.string(),
  name: z.string(),
  payload: z.object({
    broadcastSessionId: z.string(),
    version: z.string(),
    environment: z.string(),
    playerAuthId: z.string(),
    persistentId: z.string(),
    playerConnectionId: z.string(),
    inviteId: z.string(),
    qualityStats: z.array(qualityStatSchema),
    collectionInterval: z.number(),
    sourceType: z.nativeEnum(MetricSourceType), // NEW: Indicates if stats are from player or host
  }),
});

const metricsWebrtcQualityPayloadSchema = z.object({
  payload: qualityMetricsPayloadSchema,
});

const metricsWebrtcQualityChangePayloadSchema = z.object({
  qualityChange: z.string(),
});

const metricsErrorPayloadSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string().or(z.instanceof(ZodError)),
  }),
});

// Full message schemas
const metricsWebrtcQualitySchema = baseMessageSchema
  .extend({
    type: z.literal(MetricMessageType.METRICS_WEBRTC_QUALITY),
  })
  .merge(metricsWebrtcQualityPayloadSchema);

const metricsWebrtcQualityChangeSchema = baseMessageSchema
  .extend({
    type: z.literal(MetricMessageType.METRICS_WEBRTC_QUALITY_CHANGE),
  })
  .merge(metricsWebrtcQualityChangePayloadSchema);

const metricsErrorSchema = baseMessageSchema
  .extend({
    type: z.literal(MetricMessageType.METRICS_ERROR),
  })
  .merge(metricsErrorPayloadSchema);

export const metricsMessageSchema = z.discriminatedUnion('type', [
  metricsWebrtcQualitySchema,
  metricsWebrtcQualityChangeSchema,
  metricsErrorSchema,
]);

// Payload types
export type MetricWebrtcQualityPayload = z.infer<typeof metricsWebrtcQualityPayloadSchema>;
export type MetricWebrtcQualityChangePayload = z.infer<typeof metricsWebrtcQualityChangePayloadSchema>;
export type MetricErrorPayload = z.infer<typeof metricsErrorPayloadSchema>;

// Full message type
export type MetricsMessage = z.infer<typeof metricsMessageSchema>;

type PayloadType<T extends MetricMessageType> = T extends MetricMessageType.METRICS_WEBRTC_QUALITY
  ? MetricWebrtcQualityPayload
  : T extends MetricMessageType.METRICS_WEBRTC_QUALITY_CHANGE
    ? MetricWebrtcQualityChangePayload
    : T extends MetricMessageType.METRICS_ERROR
      ? MetricErrorPayload
      : never;

export function generateMetricMessage<T extends MetricMessageType>(type: T, payload: PayloadType<T>): MetricsMessage {
  const base = {
    id: createMessageId(),
    timestamp: Date.now(),
    type,
  };

  switch (type) {
    case MetricMessageType.METRICS_WEBRTC_QUALITY:
      return metricsWebrtcQualitySchema.parse({
        ...base,
        ...payload,
      });
    case MetricMessageType.METRICS_WEBRTC_QUALITY_CHANGE:
      return metricsWebrtcQualityChangeSchema.parse({
        ...base,
        ...payload,
      });
    case MetricMessageType.METRICS_ERROR:
      return metricsErrorSchema.parse({
        ...base,
        ...payload,
      });
    default: {
      const exhaustiveCheck: never = type;
      throw new Error(`Unhandled message type: ${exhaustiveCheck}`);
    }
  }
}
